import React from 'react';

import noop from '@atlaskit/ds-lib/noop';
import {
	BANNER_HEIGHT,
	LEFT_PANEL_WIDTH,
	LEFT_SIDEBAR_WIDTH,
	Banner as LegacyBanner,
	Content as LegacyContent,
	LeftPanel as LegacyLeftPanel,
	LeftSidebar as LegacyLeftSidebar,
	LeftSidebarWithoutResize as LegacyLeftSidebarWithoutResize,
	Main as LegacyMain,
	PageLayout as LegacyPageLayout,
	RightPanel as LegacyRightPanel,
	RightSidebar as LegacyRightSidebar,
	TopNavigation as LegacyTopNavigation,
	RIGHT_PANEL_WIDTH,
	RIGHT_SIDEBAR_WIDTH,
	TOP_NAVIGATION_HEIGHT,
	UNSAFE_useSetLeftSidebarState as UNSAFE_useLegacySetLeftSidebarState,
	useCustomSkipLink as useLegacyCustomSkipLink,
	useLeftSidebarFlyoutLock as useLegacyLeftSidebarFlyoutLock,
	usePageLayoutResize as useLegacyPageLayoutResize,
} from '@atlaskit/page-layout';
import { fg } from '@atlaskit/platform-feature-flags';

import {
	Aside,
	Banner as NewBanner,
	Main as NewMain,
	Panel,
	Root,
	SideNav,
	TopBar,
} from './ui/page-layout/layout';

export function PageLayout(props: React.ComponentProps<typeof LegacyPageLayout>) {
	return fg('platform.atlassian.griffin-new-page-layout') ? (
		<Root UNSAFE_dangerouslyHoistSlotSizes>{props.children}</Root>
	) : (
		<LegacyPageLayout {...props} />
	);
}

export function Banner(props: React.ComponentProps<typeof LegacyBanner>) {
	return fg('platform.atlassian.griffin-new-page-layout') ? (
		<NewBanner UNSAFE_height={props.height}>{props.children}</NewBanner>
	) : (
		<LegacyBanner {...props} />
	);
}

export function TopNavigation(props: React.ComponentProps<typeof LegacyTopNavigation>) {
	return fg('platform.atlassian.griffin-new-page-layout') ? (
		<TopBar UNSAFE_height={props.height}>{props.children}</TopBar>
	) : (
		<LegacyTopNavigation {...props} />
	);
}

export function LeftPanel(props: React.ComponentProps<typeof LegacyLeftPanel>) {
	// LeftPanel is not supported in new page layout
	return fg('platform.atlassian.griffin-new-page-layout') ? null : <LegacyLeftPanel {...props} />;
}

export function LeftSidebarWithoutResize(
	props: React.ComponentProps<typeof LegacyLeftSidebarWithoutResize>,
) {
	return fg('platform.atlassian.griffin-new-page-layout') ? (
		<SideNav defaultWidth={props.width}>{props.children}</SideNav>
	) : (
		<LegacyLeftSidebarWithoutResize {...props} />
	);
}

export function LeftSidebar(props: React.ComponentProps<typeof LegacyLeftSidebar>) {
	return fg('platform.atlassian.griffin-new-page-layout') ? (
		<SideNav defaultCollapsed={props.collapsedState === 'collapsed'} defaultWidth={props.width}>
			{props.children}
		</SideNav>
	) : (
		<LegacyLeftSidebar {...props} />
	);
}

export function Main(props: React.ComponentProps<typeof LegacyMain>) {
	return fg('platform.atlassian.griffin-new-page-layout') ? (
		<NewMain isFixed={props.isFixed}>{props.children}</NewMain>
	) : (
		<LegacyMain {...props} />
	);
}

export function Content(props: React.ComponentProps<typeof LegacyContent>) {
	return fg('platform.atlassian.griffin-new-page-layout') ? (
		// There isn't a concept of a `Content` wrapper component in the new page layout
		props.children
	) : (
		<LegacyContent {...props} />
	);
}

export function RightSidebar(props: React.ComponentProps<typeof LegacyRightSidebar>) {
	return fg('platform.atlassian.griffin-new-page-layout') ? (
		<Aside isFixed={props.isFixed} defaultWidth={props.width}>
			{props.children}
		</Aside>
	) : (
		<LegacyRightSidebar {...props} />
	);
}

export function RightPanel(props: React.ComponentProps<typeof LegacyRightPanel>) {
	return fg('platform.atlassian.griffin-new-page-layout') ? (
		<Panel defaultWidth={props.width}>{props.children}</Panel>
	) : (
		<LegacyRightPanel {...props} />
	);
}

/**
 * Hooks do not exist in the new page layout, and are no-op'd
 */

export const useCustomSkipLink: typeof useLegacyCustomSkipLink = (id, title, index) =>
	fg('platform.atlassian.griffin-new-page-layout')
		? {}
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useLegacyCustomSkipLink(id, title, index);

export const useLeftSidebarFlyoutLock: typeof useLegacyLeftSidebarFlyoutLock = () =>
	fg('platform.atlassian.griffin-new-page-layout')
		? {}
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useLegacyLeftSidebarFlyoutLock();

export const UNSAFE_useSetLeftSidebarState: typeof UNSAFE_useLegacySetLeftSidebarState = () =>
	fg('platform.atlassian.griffin-new-page-layout') ? noop : UNSAFE_useLegacySetLeftSidebarState();

// A mock value is needed to match the signature of the hook
const mockPageLayoutResize = {
	isLeftSidebarCollapsed: false,
	expandLeftSidebar: noop,
	collapseLeftSidebar: noop,
	leftSidebarState: {
		isFlyoutOpen: false,
		isResizing: false,
		isLeftSidebarCollapsed: false,
		leftSidebarWidth: 0,
		lastLeftSidebarWidth: 0,
		flyoutLockCount: 0,
		isFixed: true,
	},
	setLeftSidebarState: noop,
	toggleLeftSidebar: noop,
};

export const usePageLayoutResize: typeof useLegacyPageLayoutResize = () =>
	fg('platform.atlassian.griffin-new-page-layout')
		? mockPageLayoutResize
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useLegacyPageLayoutResize();

export const UNSAFE_DO_NOT_USE_BANNER_HEIGHT = BANNER_HEIGHT;
export const UNSAFE_DO_NOT_USE_LEFT_PANEL_WIDTH = LEFT_PANEL_WIDTH;
export const UNSAFE_DO_NOT_USE_LEFT_SIDEBAR_WIDTH = LEFT_SIDEBAR_WIDTH;
export const UNSAFE_DO_NOT_USE_RIGHT_PANEL_WIDTH = RIGHT_PANEL_WIDTH;
export const UNSAFE_DO_NOT_USE_RIGHT_SIDEBAR_WIDTH = RIGHT_SIDEBAR_WIDTH;
export const UNSAFE_DO_NOT_USE_TOP_NAVIGATION_HEIGHT = TOP_NAVIGATION_HEIGHT;

export { CreateButton } from './ui/top-navigation/create-button';
export { Help } from './ui/top-navigation/help';
export { Notifications } from './ui/top-navigation/notifications';
